import { EmptyState, ListSkeleton } from '@atlas/ui-components'
import { Badge, Button, Input, ListBoxItem, Select } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/editorial/PageHeader'
import {
	MESSAGE_STATUS_LABEL,
	MESSAGE_TYPE_LABEL,
	type Message,
	type MessageListParams,
	MessageStatus,
	MessageType,
} from '@/domain/message.types'
import { useMessages } from '@/hooks/useMessages'
import { MessageResolutionMatrix } from './MessageResolutionMatrix'
import { useMessageResolutionMatrix } from './useMessageResolutionMatrix'

const ALL = 'all'

/** How the list of messages is shown: one row per record, or one row per piece of copy against every locale. */
enum MessagesView {
	LIST = 'list',
	MATRIX = 'matrix',
}

const VIEW_LABEL: Record<MessagesView, string> = {
	[MessagesView.LIST]: 'List',
	[MessagesView.MATRIX]: 'Matrix',
}

/** The flat one-record-per-row list. Unchanged behaviour, extracted so the page can switch views. */
function FlatList({ messages, isLoading }: { messages: Message[]; isLoading: boolean }) {
	const navigate = useNavigate()
	if (isLoading) return <ListSkeleton label="Loading…" />
	if (messages.length === 0) return <EmptyState title="No messages found." />
	return (
		<>
			{messages.map((m) => (
				<button
					type="button"
					key={m.messageId}
					onClick={() => void navigate(`/messages/${m.messageId}`)}
					className="flex w-full items-center gap-3 rounded-ds-card border border-ds-border bg-ds-surface-elevated p-4 text-left hover:border-ds-border-strong"
				>
					<div className="min-w-0 flex-1">
						<p className="truncate font-medium text-ds-text">{m.title}</p>
						<p className="truncate text-sm text-ds-text-muted">{m.content}</p>
					</div>
					<Badge>{MESSAGE_TYPE_LABEL[m.messageType]}</Badge>
					<Badge variant="secondary">{m.languageIso}</Badge>
					<span className="text-xs uppercase tracking-wide text-ds-text-subtle">{MESSAGE_STATUS_LABEL[m.status]}</span>
				</button>
			))}
		</>
	)
}

/** The matrix view, wired to its container hook. Mounted only while selected so its per-group reads stop when the user switches back. */
function MatrixView({ params }: { params: MessageListParams }) {
	const matrix = useMessageResolutionMatrix(params)
	return (
		<MessageResolutionMatrix
			rows={matrix.rows}
			localeColumns={matrix.localeColumns}
			isLoading={matrix.isLoading}
			isError={matrix.isError}
			selection={matrix.selection}
			selectedCount={matrix.selectedCount}
			onToggleCell={matrix.toggleCell}
			onToggleRow={matrix.toggleRow}
			onToggleLocale={matrix.toggleLocale}
			availableLocaleOptions={matrix.availableLocaleOptions}
			onAddLocale={matrix.addLocale}
			onApplyTranslation={matrix.applyTranslation}
			pendingOverwrites={matrix.pendingOverwrites}
			onConfirmApplyOverwrites={matrix.confirmApplyOverwrites}
			onCancelApplyOverwrites={matrix.cancelApplyOverwrites}
			isApplying={matrix.isApplying}
			batchError={matrix.batchError}
			isTruncated={matrix.isTruncated}
			edit={matrix.edit}
			add={matrix.add}
		/>
	)
}

/** Messages list — search + type/status filters + link to details (route `/messages`). */
export function MessageListPage() {
	const navigate = useNavigate()
	const [search, setSearch] = useState('')
	const [type, setType] = useState<string>(ALL)
	const [status, setStatus] = useState<string>(ALL)
	const [view, setView] = useState<MessagesView>(MessagesView.LIST)

	const params: MessageListParams = {
		search: search.trim() || undefined,
		messageType: type === ALL ? undefined : (type as MessageType),
		status: status === ALL ? undefined : (status as MessageStatus),
	}
	// Only the selected view reads: the two views page the list differently, so
	// leaving both enabled would issue a second, unused request.
	const { data: messages = [], isLoading } = useMessages(params, { enabled: view === MessagesView.LIST })

	return (
		<div>
			<PageHeader
				eyebrow="Content"
				title="Messages"
				subtitle="Reusable units of copy — taglines, carousel copy, CTAs."
				actions={
					<Button className="rounded-ds-full px-5" onPress={() => void navigate('/messages/new')}>
						New message
					</Button>
				}
			/>

			<div className="mt-6 flex flex-wrap items-center gap-3">
				<Input
					aria-label="Search messages"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder="Search title or content…"
					className="max-w-xs"
				/>
				<Select aria-label="Filter by type" selectedKey={type} onSelectionChange={(k) => setType(String(k))}>
					<ListBoxItem id={ALL}>All types</ListBoxItem>
					{Object.values(MessageType).map((t) => (
						<ListBoxItem key={t} id={t}>
							{MESSAGE_TYPE_LABEL[t]}
						</ListBoxItem>
					))}
				</Select>
				<Select aria-label="Filter by status" selectedKey={status} onSelectionChange={(k) => setStatus(String(k))}>
					<ListBoxItem id={ALL}>All statuses</ListBoxItem>
					{Object.values(MessageStatus).map((s) => (
						<ListBoxItem key={s} id={s}>
							{MESSAGE_STATUS_LABEL[s]}
						</ListBoxItem>
					))}
				</Select>
				<div
					role="radiogroup"
					aria-label="View"
					className="ml-auto flex items-center gap-1 rounded-ds-full border border-ds-border p-1"
				>
					{Object.values(MessagesView).map((v) => (
						<button
							key={v}
							type="button"
							role="radio"
							aria-checked={view === v}
							onClick={() => setView(v)}
							className={
								view === v
									? 'rounded-ds-full bg-ds-primary px-3 py-1 text-sm text-ds-primary-foreground'
									: 'rounded-ds-full px-3 py-1 text-sm text-ds-text-muted hover:text-ds-text'
							}
						>
							{VIEW_LABEL[v]}
						</button>
					))}
				</div>
			</div>

			<div className="mt-6 space-y-2">
				{view === MessagesView.MATRIX ? (
					<MatrixView params={params} />
				) : (
					<FlatList messages={messages} isLoading={isLoading} />
				)}
			</div>
		</div>
	)
}
